import React, { useState, useEffect, useRef } from 'react';
import { Table, Tag, Radio } from 'antd';
import axios from 'axios';
import { ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';

const CloneOrders = () => {
  const [data, setData] = useState([]);
  const [expandedRows, setExpandedRows] = useState([]);
  const [viewMode, setViewMode] = useState('account'); // State to manage view mode
  const intervalRef = useRef();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get('http://trade-api.serpx.net:8080/api/positions');
        setData(response.data.accounts);
      } catch (error) {
        console.error('Error fetching positions:', error);
      }
    };

    fetchData();

    // Set up interval for background refresh every 5 seconds
    intervalRef.current = setInterval(fetchData, 5000);

    return () => clearInterval(intervalRef.current); // Cleanup on unmount
  }, []);

  const calculateTotals = (positions, field) => {
    return positions.reduce((total, item) => total + (item[field] || 0), 0);
  };

  const calculateAverage = (positions, field) => {
    const validPositions = positions.filter(pos => pos[field] && pos[field] > 0);
    if (validPositions.length === 0) return 0;
    const total = validPositions.reduce((sum, pos) => sum + (pos[field] * pos.size), 0);
    const totalSize = validPositions.reduce((sum, pos) => sum + pos.size, 0);
    return totalSize > 0 ? total / totalSize : 0;
  };

  // Group positions by symbol and side for symbol view
  const groupPositionsBySymbol = () => {
    const allPositions = data.flatMap((account) => 
      account.positions.map(pos => ({ ...pos, accountName: account.account }))
    );
    
    const grouped = {};
    
    allPositions.forEach(position => {
      const key = `${position.symbol}_${position.side}`;
      if (!grouped[key]) {
        grouped[key] = {
          symbol: position.symbol,
          side: position.side,
          positions: []
        };
      }
      grouped[key].positions.push(position);
    });

    // Convert to array and calculate totals for each group
    const groupedArray = Object.values(grouped).map(group => {
      const totalSize = group.positions.reduce((sum, pos) => sum + pos.size, 0);
      const avgEntryPrice = calculateAverage(group.positions, 'entryPrice');
      const currentPrice = group.positions[0]?.currentPrice || 0; // Assuming same current price for same symbol
      const totalRealizedPnl = calculateTotals(group.positions, 'realizedPnl');
      const totalUnrealizedPnl = calculateTotals(group.positions, 'unrealizedPnl');

      return {
        key: `${group.symbol}_${group.side}`,
        symbol: group.symbol,
        side: group.side,
        size: totalSize,
        entryPrice: avgEntryPrice,
        currentPrice: currentPrice,
        realizedPnl: totalRealizedPnl,
        unrealizedPnl: totalUnrealizedPnl,
        positions: group.positions.sort((a, b) => a.accountName.localeCompare(b.accountName))
      };
    });

    // Sort by symbol alphabetically, then by side
    return groupedArray.sort((a, b) => {
      const symbolCompare = a.symbol.localeCompare(b.symbol);
      if (symbolCompare !== 0) return symbolCompare;
      return a.side.localeCompare(b.side);
    });
  };

  const mainColumnsByAccount = [
    {
      title: 'Account',
      dataIndex: 'account',
      key: 'account',
      render: (account) => <strong>{account}</strong>,
    },
    {
      title: 'Total Positions',
      dataIndex: 'positionCount',
      key: 'positionCount',
    },
    {
      title: 'Realized PnL',
      key: 'realizedPnl',
      render: (_, record) => {
        const total = calculateTotals(record.positions, 'realizedPnl');
        const isProfit = total >= 0;
        return (
          <Tag
            color={isProfit ? 'green' : 'red'}
            style={{ fontWeight: 600, fontSize: 16, color: 'white', backgroundColor: isProfit ? 'green' : 'red' }}
            icon={isProfit ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
          >
            {total.toFixed(2)}
          </Tag>
        );
      },
    },
    {
      title: 'Unrealized PnL',
      key: 'unrealizedPnl',
      render: (_, record) => {
        const total = calculateTotals(record.positions, 'unrealizedPnl');
        const isProfit = total >= 0;
        return (
          <Tag
            color={isProfit ? 'green' : 'red'}
            style={{ fontWeight: 600, fontSize: 16, color: 'white', backgroundColor: isProfit ? 'green' : 'red' }}
            icon={isProfit ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
          >
            {total.toFixed(2)}
          </Tag>
        );
      },
    },
  ];

  const mainColumnsBySymbol = [
    {
      title: 'Symbol',
      dataIndex: 'symbol',
      key: 'symbol',
      render: (symbol) => <strong>{symbol.replace(/\//g, '').replace(/:USDT/g, '')}</strong>,
    },
    {
      title: 'Side',
      dataIndex: 'side',
      key: 'side',
      render: (side) => (
        <Tag
          color={side === 'long' ? 'green' : 'red'}
          style={{ fontWeight: 600, fontSize: 16, color: 'white', backgroundColor: side === 'long' ? 'green' : 'red' }}
        >
          {side.charAt(0).toUpperCase() + side.slice(1)}
        </Tag>
      ),
    },
    {
      title: 'Total Size',
      dataIndex: 'size',
      key: 'size',
      render: (size) => size.toFixed(2),
    },
    {
      title: 'Avg Entry Price',
      dataIndex: 'entryPrice',
      key: 'entryPrice',
      render: (price) => price.toFixed(8),
    },
    {
      title: 'Current Price',
      dataIndex: 'currentPrice',
      key: 'currentPrice',
      render: (price) => price ? price.toFixed(8) : '-',
    },
    {
      title: 'Total Realized PnL',
      dataIndex: 'realizedPnl',
      key: 'realizedPnl',
      render: (pnl) => {
        const isProfit = pnl >= 0;
        return (
          <Tag
            color={isProfit ? 'green' : 'red'}
            style={{ fontWeight: 600, fontSize: 16, color: 'white', backgroundColor: isProfit ? 'green' : 'red' }}
            icon={isProfit ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
          >
            {pnl ? pnl.toFixed(2) : '-'}
          </Tag>
        );
      },
    },
    {
      title: 'Total Unrealized PnL',
      dataIndex: 'unrealizedPnl',
      key: 'unrealizedPnl',
      render: (pnl) => {
        const isProfit = pnl >= 0;
        return (
          <Tag
            color={isProfit ? 'green' : 'red'}
            style={{ fontWeight: 600, fontSize: 16, color: 'white', backgroundColor: isProfit ? 'green' : 'red' }}
            icon={isProfit ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
          >
            {pnl ? pnl.toFixed(2) : '-'}
          </Tag>
        );
      },
    },
  ];

  const expandedRowRender = (positions) => {
    const subData = positions.sort((a, b) => a.symbol.localeCompare(b.symbol));

    const subColumns = [
      {
        title: 'Symbol',
        dataIndex: 'symbol',
        key: 'symbol',
        render: (symbol) => symbol.replace(/\//g, '').replace(/:USDT/g, ''),
      },
      {
        title: 'Side',
        dataIndex: 'side',
        key: 'side',
        render: (side) => (
          <Tag
            color={side === 'long' ? 'green' : 'red'}
            style={{ fontWeight: 600, fontSize: 16, color: 'white', backgroundColor: side === 'long' ? 'green' : 'red' }}
          >
            {side.charAt(0).toUpperCase() + side.slice(1)}
          </Tag>
        ),
      },
      {
        title: 'Size',
        dataIndex: 'size',
        key: 'size',
      },
      {
        title: 'Entry Price',
        dataIndex: 'entryPrice',
        key: 'entryPrice',
      },
      {
        title: 'Current Price',
        dataIndex: 'currentPrice',
        key: 'currentPrice',
      },
      {
        title: 'Realized PnL',
        dataIndex: 'realizedPnl',
        key: 'realizedPnl',
        render: (pnl) => {
          const isProfit = pnl >= 0;
          return (
            <Tag
              color={isProfit ? 'green' : 'red'}
              style={{ fontWeight: 600, fontSize: 16, color: 'white', backgroundColor: isProfit ? 'green' : 'red' }}
              icon={isProfit ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
            >
              {pnl ? pnl.toFixed(2) : '-'}
            </Tag>
          );
        },
      },
      {
        title: 'Unrealized PnL',
        dataIndex: 'unrealizedPnl',
        key: 'unrealizedPnl',
        render: (pnl) => {
          const isProfit = pnl >= 0;
          return (
            <Tag
              color={isProfit ? 'green' : 'red'}
              style={{ fontWeight: 600, fontSize: 16, color: 'white', backgroundColor: isProfit ? 'green' : 'red' }}
              icon={isProfit ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
            >
              {pnl ? pnl.toFixed(2) : '-'}
            </Tag>
          );
        },
      },
    ];

    return <Table columns={subColumns} dataSource={subData} pagination={false} rowKey="symbol" />;
  };

  // Expanded row render for symbol view - shows individual accounts
  const expandedRowRenderBySymbol = (positions) => {
    const subColumns = [
      {
        title: 'Account',
        dataIndex: 'accountName',
        key: 'accountName',
        render: (accountName) => <strong>{accountName}</strong>,
      },
      {
        title: 'Side',
        dataIndex: 'side',
        key: 'side',
        render: (side) => (
          <Tag
            color={side === 'long' ? 'green' : 'red'}
            style={{ fontWeight: 600, fontSize: 16, color: 'white', backgroundColor: side === 'long' ? 'green' : 'red' }}
          >
            {side.charAt(0).toUpperCase() + side.slice(1)}
          </Tag>
        ),
      },
      {
        title: 'Size',
        dataIndex: 'size',
        key: 'size',
      },
      {
        title: 'Entry Price',
        dataIndex: 'entryPrice',
        key: 'entryPrice',
      },
      {
        title: 'Current Price',
        dataIndex: 'currentPrice',
        key: 'currentPrice',
      },
      {
        title: 'Realized PnL',
        dataIndex: 'realizedPnl',
        key: 'realizedPnl',
        render: (pnl) => {
          const isProfit = pnl >= 0;
          return (
            <Tag
              color={isProfit ? 'green' : 'red'}
              style={{ fontWeight: 600, fontSize: 16, color: 'white', backgroundColor: isProfit ? 'green' : 'red' }}
              icon={isProfit ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
            >
              {pnl ? pnl.toFixed(2) : '-'}
            </Tag>
          );
        },
      },
      {
        title: 'Unrealized PnL',
        dataIndex: 'unrealizedPnl',
        key: 'unrealizedPnl',
        render: (pnl) => {
          const isProfit = pnl >= 0;
          return (
            <Tag
              color={isProfit ? 'green' : 'red'}
              style={{ fontWeight: 600, fontSize: 16, color: 'white', backgroundColor: isProfit ? 'green' : 'red' }}
              icon={isProfit ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
            >
              {pnl ? pnl.toFixed(2) : '-'}
            </Tag>
          );
        },
      },
    ];

    return <Table columns={subColumns} dataSource={positions} pagination={false} rowKey={(record) => `${record.accountName}_${record.symbol}_${record.side}`} />;
  };

  const handleExpand = (expanded, record) => {
    const key = viewMode === 'account' ? record.account : record.key;
    if (expanded) {
      setExpandedRows([...expandedRows, key]);
    } else {
      setExpandedRows(expandedRows.filter((row) => row !== key));
    }
  };

  const renderTable = () => {
    if (viewMode === 'account') {
      return (
        <Table
          columns={mainColumnsByAccount}
          dataSource={data}
          expandable={{
            expandedRowRender: (record) => expandedRowRender(record.positions),
            onExpand: handleExpand,
            expandedRowKeys: expandedRows,
          }}
          rowKey="account"
        />
      );
    } else {
      const groupedPositions = groupPositionsBySymbol();
      return (
        <Table
          columns={mainColumnsBySymbol}
          dataSource={groupedPositions}
          expandable={{
            expandedRowRender: (record) => expandedRowRenderBySymbol(record.positions),
            onExpand: handleExpand,
            expandedRowKeys: expandedRows,
          }}
          rowKey="key"
        />
      );
    }
  };

  return (
    <div>
      <Radio.Group
        value={viewMode}
        onChange={(e) => {
          setViewMode(e.target.value);
          setExpandedRows([]); // Clear expanded rows when switching views
        }}
        style={{ marginBottom: 16 }}
      >
        <Radio.Button value="account">View by Account</Radio.Button>
        <Radio.Button value="symbol">View by Symbol</Radio.Button>
      </Radio.Group>
      {renderTable()}
    </div>
  );
};

export default CloneOrders;